import * as Y from 'yjs';
import { mapToObject } from './use-yjs-snapshot';

/** The Yjs root key holding a document's surface manifest. */
export const SURFACES_ROOT = 'surfaces';

export type SurfaceSourceKind = 'document' | 'transcript' | 'surface' | 'calendar_event';

/** What a generated surface read, as the server records it on the entry. */
export interface SurfaceSource {
  kind: SurfaceSourceKind;
  title: string;
  entityId?: string;
  recordingId?: string;
  surfaceId?: string;
  eventId?: string;
  excerpt?: true;
}

/**
 * One entry in a document's surface manifest, as the server reads it
 * (`services/document_tabs/manifest.rs`). `surface`, `source` and `status` are
 * open strings rather than unions because the server stores them as free strings
 * and new members are expected.
 */
export interface SurfaceEntry {
  id: string;
  title: string;
  fragment: string;
  surface: string;
  source: string;
  order: string;
  editable: boolean;
  /** Template key that produced this surface; null means a person authored it. */
  generator: string | null;
  /** 'generating' | 'streaming' | 'ready' | 'failed' | 'stale' | 'regenerating' | 'proposed' */
  status: string;
  /** The recording this surface holds, when it holds one. */
  recordingId: string | null;
  /** Why the run failed; present only on a failed surface, and only when the server wrote it. */
  failure?: string;
  /** Retry number while a run is retrying (2, 3, ...). */
  attempt?: number;
  requestedBy?: string;
  /** The template version that wrote the content; absent on surfaces older than versioning. */
  templateVersion?: number;
  /** Why the last regeneration failed; the content is intact and `status` stays 'ready'. */
  regenerationFailure?: string;
  /** What the run read; present only when the server wrote at least one valid source. */
  sources?: SurfaceSource[];
}

export type SurfaceDisplayStatus = 'generating' | 'failed' | 'ready' | 'regenerating' | 'proposed';

/** `status` is an open set: anything unrecognised is content, never progress. */
export function surfaceDisplayStatus(entry: SurfaceEntry): SurfaceDisplayStatus {
  if (entry.status === 'generating' || entry.status === 'streaming') return 'generating';
  if (entry.status === 'failed') return 'failed';
  if (entry.status === 'regenerating') return 'regenerating';
  if (entry.status === 'proposed') return 'proposed';
  return 'ready';
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

const SOURCE_KINDS: readonly string[] = ['document', 'transcript', 'surface', 'calendar_event'];
const SOURCE_IDS = ['entityId', 'recordingId', 'surfaceId', 'eventId'] as const;

function plain(value: unknown): unknown {
  return value instanceof Y.AbstractType ? value.toJSON() : value;
}

function projectSource(value: unknown): SurfaceSource | null {
  const fields = plain(value);
  if (typeof fields !== 'object' || fields === null || Array.isArray(fields)) return null;
  const { kind, title } = fields as Record<string, unknown>;
  if (typeof kind !== 'string' || !SOURCE_KINDS.includes(kind)) return null;
  if (typeof title !== 'string' || title.trim() === '') return null;
  const source: SurfaceSource = { kind: kind as SurfaceSourceKind, title };
  for (const key of SOURCE_IDS) {
    const id = (fields as Record<string, unknown>)[key];
    if (typeof id === 'string') source[key] = id;
  }
  if ((fields as Record<string, unknown>).excerpt === true) source.excerpt = true;
  return source;
}

function projectSources(value: unknown): SurfaceSource[] {
  const items = plain(value);
  if (!Array.isArray(items)) return [];
  return items.map(projectSource).filter((s): s is SurfaceSource => s !== null);
}

function project(fields: Record<string, unknown>): SurfaceEntry | null {
  const { id, fragment } = fields;
  if (typeof id !== 'string' || typeof fragment !== 'string') return null;
  const entry: SurfaceEntry = {
    id,
    fragment,
    title: str(fields.title, ''),
    surface: str(fields.surface, 'text'),
    source: str(fields.source, 'owned'),
    order: str(fields.order, ''),
    editable: typeof fields.editable === 'boolean' ? fields.editable : true,
    generator: typeof fields.generator === 'string' ? fields.generator : null,
    status: str(fields.status, 'ready'),
    recordingId: typeof fields.recordingId === 'string' ? fields.recordingId : null,
  };
  if (typeof fields.failure === 'string') entry.failure = fields.failure;
  if (typeof fields.attempt === 'number' && Number.isInteger(fields.attempt) && fields.attempt >= 1) {
    entry.attempt = fields.attempt;
  }
  if (
    typeof fields.templateVersion === 'number' &&
    Number.isInteger(fields.templateVersion) &&
    fields.templateVersion >= 1
  ) {
    entry.templateVersion = fields.templateVersion;
  }
  if (typeof fields.regenerationFailure === 'string' && fields.regenerationFailure !== '') {
    entry.regenerationFailure = fields.regenerationFailure;
  }
  if (typeof fields.requestedBy === 'string') entry.requestedBy = fields.requestedBy;
  const sources = projectSources(fields.sources);
  if (sources.length > 0) entry.sources = sources;
  return entry;
}

/**
 * The document's surfaces, ordered. Entries missing `id` or `fragment` are
 * skipped — the manifest is client-written with no server validation in front of
 * it (design §5.1), so a malformed entry must not break the list. Ties on `order`
 * break on `id` so every client converges on the same order (design §4.3).
 */
export function readSurfaces(doc: Y.Doc | null): SurfaceEntry[] {
  if (!doc) return [];
  const root = doc.getArray<unknown>(SURFACES_ROOT);
  const out: SurfaceEntry[] = [];
  for (const element of root) {
    if (!(element instanceof Y.Map)) continue;
    const entry = project(mapToObject(element as Y.Map<unknown>));
    if (entry) out.push(entry);
  }
  // Ordinal (byte-wise), not localeCompare: must match the server's
  // `a.id.cmp(&b.id)` (manifest.rs:60), which is Rust String::cmp over UTF-8
  // bytes. localeCompare case-folds ('a' < 'B'), so it silently disagrees with
  // byte-wise comparison ('B' < 'a') whenever two ids differ only in case —
  // do not "simplify" this back to localeCompare.
  return out.sort((a, b) => (a.order === b.order
    ? (a.id < b.id ? -1 : 1)
    : a.order < b.order ? -1 : 1));
}
